"use client";

import { useEffect, useId, useRef, useState } from "react";
import { AudioLines, Check, ExternalLink, LoaderCircle } from "lucide-react";
import { type SavedPublicVoice, type TtsModelId } from "@reelmagic/domain";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { savePublicVoice } from "@/lib/generation/public-voices";

const inputClass = "h-12 w-full rounded-xl border border-border bg-white/[0.035] px-3.5 text-sm text-foreground outline-none transition placeholder:text-[var(--rm-text-muted)] focus:border-ring focus:ring-2 focus:ring-ring/25 disabled:opacity-55";

export function PublicVoiceDialog({ open, onOpenChange, modelId, onSaved }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  modelId: TtsModelId;
  onSaved: (voice: SavedPublicVoice) => void;
}) {
  const id = useId();
  const [voiceId, setVoiceId] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);
  const validId = /^[a-zA-Z0-9]{20}$/.test(voiceId.trim());

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  useEffect(() => {
    if (!open) { setVoiceId(""); setLabel(""); setError(null); }
  }, [open]);

  async function save() {
    if (busy || !validId || !label.trim()) return;
    setBusy(true); setError(null);
    try {
      const saved = await savePublicVoice({ modelId, providerVoiceId: voiceId.trim(), label: label.trim() });
      if (mounted.current) { onSaved(saved); onOpenChange(false); }
    } catch (cause) {
      if (mounted.current) setError(cause instanceof Error
        ? cause.message.replace(/^Firebase:\s*/, "").replace(/\s*\(functions\/[^)]+\)\.?$/u, "")
        : "This voice could not be saved. Try again.");
    } finally {
      if (mounted.current) setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => { if (!busy) onOpenChange(nextOpen); }}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] max-w-[460px] gap-6 overflow-y-auto rounded-[24px] border-white/10 p-6 sm:p-7" showCloseButton={!busy}>
        <DialogHeader className="gap-3">
          <span className="flex size-11 items-center justify-center rounded-[14px] border border-primary/20 bg-primary/10 text-primary"><AudioLines className="size-5" /></span>
          <div className="grid gap-1.5">
            <DialogTitle>Add a public voice</DialogTitle>
            <DialogDescription>Paste a voice ID from the ElevenLabs library and give it a name for your reels.</DialogDescription>
          </div>
        </DialogHeader>
        <form className="grid gap-6" onSubmit={(event) => { event.preventDefault(); void save(); }}>
          <div className="grid gap-5">
            <div className="grid gap-2">
              <div className="flex items-center justify-between gap-3">
                <label className="text-xs font-semibold text-[var(--rm-text-soft)]" htmlFor={`${id}-voice`}>ElevenLabs voice ID</label>
                <a className="inline-flex items-center gap-1 text-xs text-[var(--rm-text-muted)] transition hover:text-foreground" href="https://elevenlabs.io/app/voice-library" target="_blank" rel="noopener noreferrer">Voice library <ExternalLink className="size-3" /></a>
              </div>
              <input autoComplete="off" spellCheck={false} id={`${id}-voice`} className={`${inputClass} font-mono`} placeholder="Paste the public voice ID" maxLength={100} value={voiceId} disabled={busy} onChange={(event) => { setVoiceId(event.target.value); setError(null); }} aria-describedby={`${id}-help`} />
              <p id={`${id}-help`} className="text-xs leading-5 text-[var(--rm-text-muted)]">Public library voices only. Use the 20-character voice ID, not the page link.</p>
            </div>
            <div className="grid gap-2">
              <label className="text-xs font-semibold text-[var(--rm-text-soft)]" htmlFor={`${id}-name`}>Voice name</label>
              <input id={`${id}-name`} className={inputClass} placeholder="Give this voice a name" maxLength={40} value={label} disabled={busy} onChange={(event) => setLabel(event.target.value)} />
            </div>
            {error ? <p role="alert" className="rounded-xl border border-destructive/20 bg-destructive/10 px-3.5 py-3 text-sm leading-5 text-destructive">{error}</p> : null}
          </div>
          <div className="flex items-center justify-end gap-2 border-t border-white/[0.07] pt-5">
            <Button className="rounded-xl" variant="ghost" disabled={busy} onClick={() => onOpenChange(false)} type="button">Cancel</Button>
            <Button className="h-11 rounded-xl px-5" disabled={busy || !validId || !label.trim()} type="submit">
              {busy ? <LoaderCircle className="animate-spin" /> : <Check />}
              {busy ? "Checking voice…" : "Save and use voice"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
